import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import type { SigningDocumentType } from "@aam/shared";
import { SIGNING_DOCUMENT_DETAILS } from "@aam/shared";
import { getSessionToken, useSession } from "../../hooks/useSession.js";
import { useToast } from "../../context/ToastContext.js";
import { Breadcrumbs, Card, CardDescription, CardTitle, LoadingStatus } from "../ui/index.js";
import { SetupStepHeader } from "./SetupStepHeader.js";
import type { SetupStepId } from "./setupSteps.js";
import {
  applySigningPrefill,
  buildSigningEmbedDocument,
  getSigningEmbedConfig,
  useSigningEmbedListener,
} from "../../lib/signingEmbed.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

const SETUP_STEP_BY_DOCUMENT: Record<SigningDocumentType, SetupStepId> = {
  ACH_FORM: "achForm",
  CC_AUTH_FORM: "ccAuthForm",
};

export interface PortalDocumentSigningPageProps {
  documentType: SigningDocumentType;
  /**
   * "page" is the standalone document route; "step" is the same form rendered
   * as step 5 or 6 inside the Setup page.
   */
  variant?: "page" | "step";
  /** In "step" mode, moves the Setup page on once the signature is recorded. */
  onStepComplete?: () => void;
}

export function PortalDocumentSigningPage({
  documentType,
  variant = "page",
  onStepComplete,
}: PortalDocumentSigningPageProps) {
  const token = getSessionToken();
  const { agent } = useSession();
  const { success } = useToast();
  const navigate = useNavigate();
  const details = SIGNING_DOCUMENT_DETAILS[documentType];
  const embed = getSigningEmbedConfig(documentType);
  const embedScriptSrc = embed.scriptSrc
    ? applySigningPrefill(embed.scriptSrc, { firstName: agent?.firstName, lastName: agent?.lastName })
    : null;
  const [status, setStatus] = useState<"NOT_STARTED" | "SIGNED" | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void fetch(`${API_BASE}/api/signing-documents`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (response) => {
        const json = await response.json();
        if (!response.ok || !json.success) throw new Error(json.error || "Unable to load document status");
        return json.data as Array<{ documentType: SigningDocumentType; status: "NOT_STARTED" | "SIGNED" }>;
      })
      .then((entries) => {
        if (cancelled) return;
        setStatus(entries.find((entry) => entry.documentType === documentType)?.status ?? "NOT_STARTED");
      })
      .catch(() => {
        if (!cancelled) setError("Unable to load your document status");
      });
    return () => {
      cancelled = true;
    };
  }, [token, documentType]);

  const markSigned = useCallback(() => {
    // Skip if unauthenticated or already recorded — the completion listener
    // can fire more than once per signature.
    if (!token || status === "SIGNED") return;
    void fetch(`${API_BASE}/api/signing-documents/${documentType}/signed`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        const json = await response.json();
        if (!response.ok || !json.success) throw new Error(json.error || "Unable to save your signature");
        setStatus("SIGNED");
        setError(null);
        success(`${details.label} signed successfully!`);
        setTimeout(() => (onStepComplete ? onStepComplete() : navigate("/my-agent")), 1500);
      })
      .catch(() => {
        setError("We couldn't confirm your signature. Try again in a moment.");
      });
  }, [token, documentType, status, success, navigate, onStepComplete, details.label]);

  // Only listen while unsigned — no need to keep reacting after it's recorded.
  useSigningEmbedListener(Boolean(embedScriptSrc) && status === "NOT_STARTED", markSigned);

  const breadcrumbs = (
    <Breadcrumbs
      items={[
        { label: "Setup", to: "/my-agent" },
        { label: details.label, current: true },
      ]}
    />
  );

  if (status === undefined) {
    return (
      <section role="status" aria-busy="true" className="space-y-8">
        <LoadingStatus label={`Loading ${details.label}`} />
      </section>
    );
  }

  return (
    <section className="space-y-8">
      <motion.div
        className={variant === "step" ? "contents" : "border-b-2 border-border-default pb-7"}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {variant === "step" ? (
          <SetupStepHeader
            stepId={SETUP_STEP_BY_DOCUMENT[documentType]}
            complete={status === "SIGNED"}
          />
        ) : (
          breadcrumbs
        )}
      </motion.div>

      {error && <p role="alert" className="text-sm font-semibold text-status-rejected-text">{error}</p>}

      {status === "SIGNED" ? (
        <p
          role="status"
          className="rounded-xl border border-status-active-border bg-status-active-bg px-4 py-3 text-sm font-semibold text-status-active-text"
        >
          Signed — thanks! Nothing else is needed here.
        </p>
      ) : (
        <Card padding="lg" className="space-y-4 border-2">
          <div>
            <CardTitle className="text-xl font-extrabold">Sign below</CardTitle>
            <CardDescription className="mt-1">
              Complete the form in the embedded window. We'll pick up automatically once it's signed.
            </CardDescription>
          </div>

          {embedScriptSrc ? (
            <iframe
              title={details.label}
              srcDoc={buildSigningEmbedDocument(embedScriptSrc)}
              className="h-[850px] w-full rounded-card border-2 border-border-default"
            />
          ) : (
            <div className="flex h-[360px] w-full flex-col items-center justify-center gap-2 rounded-card border-2 border-dashed border-border-default bg-surface-overlay text-center">
              <p className="text-sm font-semibold text-content-primary">Signing link coming soon</p>
              <p className="max-w-sm text-xs font-medium text-content-secondary">
                This document isn't linked up yet — check back shortly.
              </p>
            </div>
          )}
        </Card>
      )}
    </section>
  );
}
